import clsx from "clsx";

// Interfaces
import { ISideTreeBadge, ISideTreeIcon, ISideTreeLabel } from "../../interfaces";

// Components
import SideTreeInformation from "../SideTreeInformation";
import SideTreeIcon from "../SideTreeIcon";
import SideTreeBadge from "../SideTreeBadge";

// MUI
import { Stack, Typography } from "@mui/material";
import { cloneElement, Fragment, isValidElement } from "react";

const isPresentRightSection = (icon: ISideTreeIcon | null = null, badge: ISideTreeBadge | null = null, isParent = false) =>
	(Boolean(icon) || Boolean(badge)) && !isParent;

const shouldShowMenu = (menu: ISideTreeLabel["menu"] | null | undefined, hovered: boolean, isParent: boolean) => {
	if (isParent) {
		return false;
	}

	return Boolean(menu?.component) && Boolean(isValidElement(menu?.component)) && hovered;
};
const getOrCreateBadge = (badge: ISideTreeBadge | null, itemCount?: string): ISideTreeBadge | null => {
	if (badge) {
		return badge;
	}
	if (itemCount) {
		return { title: itemCount, variant: "string" as const };
	}
	return null;
};

export default function SideTreeLabel(props: Readonly<ISideTreeLabel>) {
	const {
		title = "",
		information = null,
		icon = null,
		badge = null,
		itemCount,
		bold = false,
		controlled = false,
		disabled = false,
		hovered = false,
		selected = false,
		menu = null,
		isParent = false,
	} = props;

	const itemBadge = getOrCreateBadge(badge, itemCount);

	return (
		<Stack direction={"row"} alignItems={"center"}>
			{Boolean(information) && <SideTreeInformation {...information} display={hovered} selected={selected} />}
			<Stack sx={{ minWidth: 0 }}>
				<Typography
					className={clsx({
						"!py-[1px] !pr-[2px] !font-sans !text-default": true,
						"!font-bold": bold,
						"!text-white": !disabled && selected,
						"!text-disabled": disabled,
						"!select-none !underline": controlled,
						"!text-primary": controlled && !disabled,
					})}
					fontSize={"14.5px"}
					noWrap
				>
					{title}
				</Typography>
			</Stack>

			{isPresentRightSection(icon, itemBadge, isParent) && (
				<Stack direction={"row"} alignContent={"center"} justifyContent={"flex-end"} ml={"5px"} spacing={0.5} sx={{ flexGrow: 1 }}>
					{shouldShowMenu(menu, hovered, isParent) && (
						<Fragment>
							{cloneElement(menu?.component as React.ReactElement, {
								...menu,
								disabled,
								shouldShowMenu: hovered,
								selected,
							})}
						</Fragment>
					)}
					{Boolean(icon) && (
						<Stack direction={"row"} alignItems={"center"}>
							<SideTreeIcon {...icon} itemProps={props} disabled={disabled} selected={selected} />
						</Stack>
					)}
					{Boolean(itemBadge && !controlled) && (
						<Stack direction={"row"} alignItems={"center"}>
							<SideTreeBadge {...itemBadge} itemProps={props} disabled={disabled} selected={selected} />
						</Stack>
					)}
				</Stack>
			)}
		</Stack>
	);
}
